/**
 * Mail Template Service
 * Handles API requests related to mail template management
 */
import { createService } from './serviceFactory';

// Define the base URL for mail template service
const baseUrl = '/api/supplier/app-mail-template';

// Create base service with standard CRUD operations
const baseService = createService(baseUrl);

const mailTemplateService = {
  // Include all methods from the base service
  ...baseService,
  
  /**
   * Get mail template by ID
   * @param {string} id - Mail template ID
   * @returns {Promise} - Response with mail template details
   */
  getMailTemplateById(id) {
    if (!id) {
      console.error('getMailTemplateById: id is required');
      return Promise.reject(new Error('Mail template ID is required'));
    }
    
    return baseService.client.get(`${baseUrl}/${id}`)
      .then(response => response.data);
  },

  /**
   * Create new mail template
   * @param {Object} templateData - Mail template data
   * @returns {Promise} - Response with created mail template
   */
  createMailTemplate(templateData) {
    if (!templateData) {
      console.error('createMailTemplate: templateData is required');
      return Promise.reject(new Error('Mail template data is required'));
    }
    
    return baseService.client.post(`${baseUrl}`, templateData)
      .then(response => response.data);
  },

  /**
   * Update mail template
   * @param {string} id - Mail template ID
   * @param {Object} templateData - Mail template data
   * @returns {Promise} - Response with updated mail template
   */
  updateMailTemplate(id, templateData) {
    if (!id) {
      console.error('updateMailTemplate: id is required');
      return Promise.reject(new Error('Mail template ID is required'));
    }
    
    if (!templateData) {
      console.error('updateMailTemplate: templateData is required');
      return Promise.reject(new Error('Mail template data is required'));
    }
    
    return baseService.client.put(`${baseUrl}/${id}`, templateData)
      .then(response => response.data);
  },

  /**
   * Delete mail template
   * @param {string} id - Mail template ID
   * @returns {Promise} - Response confirmation
   */
  deleteMailTemplate(id) {
    if (!id) {
      console.error('deleteMailTemplate: id is required');
      return Promise.reject(new Error('Mail template ID is required'));
    }
    
    return baseService.client.delete(`${baseUrl}/${id}`)
      .then(response => response.data);
  },

  /**
   * Search mail templates. The API is ABP-paged, so it reads skipCount/maxResultCount
   * (page/pageSize are ignored and would fall back to 10 rows).
   * @param {Object} searchParams - { searchText?, templateKey?, cultureName?, sorting?, skipCount, maxResultCount }
   * @returns {Promise} - { totalCount, items }
   */
  searchMailTemplates(searchParams = {}) {
    const defaultParams = {
      searchText: '',
      sorting: '',
      skipCount: 0,
      maxResultCount: 1000
    };

    const params = { ...defaultParams, ...searchParams };

    return baseService.client.post(`${baseUrl}/search`, params)
      .then(response => response.data);
  },

  /**
   * Languages a mail template can be translated into.
   * @returns {Promise} - [{ cultureName, displayName, isDefault }]
   */
  getCultures() {
    return baseService.client.get(`${baseUrl}/cultures`)
      .then(response => response.data);
  },

  /**
   * Get mail template by key
   * @param {string} tenantId - Tenant ID
   * @param {string} templateKey - Template key
   * @param {boolean} includeHostFallback - Include host fallback
   * @returns {Promise} - Response with mail template
   */
  getMailTemplateByKey(tenantId, templateKey, includeHostFallback = true) {
    if (!tenantId) {
      console.error('getMailTemplateByKey: tenantId is required');
      return Promise.reject(new Error('Tenant ID is required'));
    }
    
    return baseService.client.get(`${baseUrl}/by-key/${tenantId}`, {
      params: {
        templateKey,
        includeHostFallback
      }
    })
      .then(response => response.data);
  },

  /**
   * Restore one language of a mail template to its default (file) version
   * @param {string} templateKey - Template key
   * @param {string} cultureName - Language to restore, e.g. 'en' or 'tr'
   * @returns {Promise} - Response confirmation
   */
  restoreHostTemplateByKey(templateKey, cultureName = 'en') {
    if (!templateKey) {
      console.error('restoreHostTemplateByKey: templateKey is required');
      return Promise.reject(new Error('Template key is required'));
    }

    return baseService.client.post(`${baseUrl}/restore-host-template-by-key`, null, {
      params: {
        templateKey,
        cultureName
      }
    })
      .then(response => response.data);
  }
};

export default mailTemplateService;
